function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

function isRequestedDesktopModeOnMobile() {
  const ua = navigator.userAgent || '';
  const isTouchDevice = (navigator.maxTouchPoints || 0) > 0;
  if (!isTouchDevice) return false;

  const hasDesktopToken = /(Macintosh|Windows NT|X11|Linux x86_64)/i.test(ua);
  const hasMobileToken = /(Mobi|Android|iPhone|iPad|iPod|Windows Phone)/i.test(ua);

  return hasDesktopToken && !hasMobileToken;
}

window.isRequestedDesktopModeOnMobile = isRequestedDesktopModeOnMobile;

function syncRequestedDesktopModeBodyClass() {
  const isRequestedDesktopMode = Boolean(window.isRequestedDesktopModeOnMobile?.());
  document.body?.classList.toggle('is-requested-desktop-mode-on-mobile', isRequestedDesktopMode);
  document.dispatchEvent(
    new CustomEvent('requested-desktop-mode:change', {
      detail: { isRequestedDesktopMode },
    })
  );
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', syncRequestedDesktopModeBodyClass);
} else {
  syncRequestedDesktopModeBodyClass();
}

window.addEventListener('resize', syncRequestedDesktopModeBodyClass);
window.addEventListener('orientationchange', syncRequestedDesktopModeBodyClass);

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');
    this._previousBodyPaddingRight = '';
    this._scrollLocked = false;

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)').matches) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    this.lockBodyScroll();
    if (!window.matchMedia('(max-width: 989px)').matches) {
      this.openActivePath();
    }
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    this.unlockBodyScroll();
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }

  lockBodyScroll() {
    if (this._scrollLocked) return;
    this._previousBodyPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth = Math.max(window.innerWidth - document.documentElement.clientWidth, 0);
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
    this._scrollLocked = true;
  }

  unlockBodyScroll() {
    if (!this._scrollLocked) return;
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    document.body.style.paddingRight = this._previousBodyPaddingRight;
    this._scrollLocked = false;
  }

  getDirectSummary(detailsElement) {
    return Array.from(detailsElement.children).find((child) => child.tagName === 'SUMMARY');
  }

  openActivePath() {
    const mobileMenuRoot = this.mainDetailsToggle.querySelector('.menu-drawer__menu--mobile-only');
    if (!mobileMenuRoot) return;

    let activeNode = mobileMenuRoot.querySelector(
      'details a.menu-drawer__menu-item[aria-current="page"], details .menu-drawer__menu-item.menu-drawer__menu-item--active'
    );

    if (!activeNode) {
      const normalizePath = (value) => {
        if (!value) return '';
        try {
          const url = new URL(value, window.location.origin);
          const path = url.pathname.replace(/\/+$/, '');
          return path || '/';
        } catch (_error) {
          return '';
        }
      };

      const extractCollectionHandle = (path) => {
        const match = path.match(/\/collections\/([^/?#]+)/i);
        return match ? decodeURIComponent(match[1]).toLowerCase() : '';
      };

      const currentPath = normalizePath(window.location.pathname);
      const currentCollectionHandle = extractCollectionHandle(currentPath);
      const nestedLinks = Array.from(mobileMenuRoot.querySelectorAll('details a.menu-drawer__menu-item'));

      activeNode = nestedLinks.find((link) => {
        const hrefPath = normalizePath(link.getAttribute('href'));
        if (!hrefPath) return false;

        const hrefCollectionHandle = extractCollectionHandle(hrefPath);
        if (currentCollectionHandle && hrefCollectionHandle) {
          return currentCollectionHandle === hrefCollectionHandle;
        }

        return currentPath === hrefPath;
      });
    }

    if (!activeNode) return;

    let detailsElement = activeNode.closest('details');
    while (detailsElement && detailsElement !== this.mainDetailsToggle) {
      detailsElement.setAttribute('open', '');
      detailsElement.classList.add('menu-opening');

      const summaryElement = this.getDirectSummary(detailsElement);
      if (summaryElement) summaryElement.setAttribute('aria-expanded', true);

      const parentMenuElement = detailsElement.closest('.has-submenu');
      if (parentMenuElement) parentMenuElement.classList.add('submenu-open');

      detailsElement = detailsElement.parentElement ? detailsElement.parentElement.closest('details') : null;
    }
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  getHeaderBottomPosition(summaryElement = null) {
    const isMobile = window.matchMedia('(max-width: 989px)').matches;
    if (isMobile) {
      const summary =
        summaryElement || this.mainDetailsToggle?.querySelector(':scope > summary') || this.querySelector('summary');
      if (summary) {
        return parseInt(summary.getBoundingClientRect().bottom - this.borderOffset);
      }
    }

    if (this.header) {
      return parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset);
    }

    return 0;
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${this.getHeaderBottomPosition(summaryElement)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    this.lockBodyScroll();
    if (!window.matchMedia('(max-width: 989px)').matches) {
      this.openActivePath();
    }
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${this.getHeaderBottomPosition()}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;
    this.isFadeMode = this.slider?.classList.contains('slideshow--fade');
    this.fadeDuration = 800;
    this.progressContainer = this.querySelector('.slideshow__progress');
    this.progressSegments = Array.from(this.querySelectorAll('.slideshow__progress-segment'));
    this.autoplayRemainingTime = null;
    this.autoplayStartedAt = null;
    this.autoplayDelay = null;
    this.manualPauseByUser = false;
    this.pauseAfterCurrentProgress = false;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.progressSegments.forEach((segment) => {
      segment.addEventListener('click', () => {
        const nextPage = Number(segment.dataset.slideIndex);
        if (!nextPage) return;
        if (this.isFadeMode) {
          this.goToSlide(nextPage);
        } else {
          const nextSlide = this.sliderItemsToShow[nextPage - 1];
          if (nextSlide) this.setSlidePosition(nextSlide.offsetLeft);
        }
        this.restartAutoplayTimer();
      });
    });
    if (!this.isFadeMode) {
      this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    }
    this.setSlideVisibility();
    this.updateProgressSegments({
      restartAnimation: this.isAutoRotateEnabled(),
      animate: this.isAutoRotateEnabled() && (!this.sliderAutoplayButton || this.autoplayButtonIsSetToPlay),
    });
    this.addEventListener('slideChanged', () => {
      this.updateProgressSegments({
        restartAnimation: this.isAutoRotateEnabled(),
        animate: this.isAutoRotateEnabled() && (!this.sliderAutoplayButton || this.autoplayButtonIsSetToPlay),
      });
    });

    if (this.isAutoRotateEnabled()) {
      this.progressContainer?.style.setProperty('--slideshow-progress-duration', `${this.slider.dataset.speed * 1000}ms`);
    }

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
    this.initSwipeGestures();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.play();
    }
  }

  onButtonClick(event) {
    if (this.isFadeMode) {
      this.wasClicked = true;
      const isNextButton = event.currentTarget.name === 'next';
      const lastSlideIndex = this.sliderItemsToShow.length || this.sliderItems.length;
      if (!lastSlideIndex) return;
      if (!this.currentPage || this.currentPage > lastSlideIndex) this.currentPage = 1;
      const nextPage = isNextButton
        ? (this.currentPage % lastSlideIndex) + 1
        : ((this.currentPage + lastSlideIndex - 2) % lastSlideIndex) + 1;

      this.goToSlide(nextPage);
      this.applyAnimationToAnnouncementBar(isNextButton ? 'next' : 'previous');
      this.requestPauseAfterProgressComplete();
      return;
    }

    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      this.requestPauseAfterProgressComplete();
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition, {
      instant: isLastSlide && event.currentTarget.name === 'next',
    });

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
    this.requestPauseAfterProgressComplete();
  }

  setSlidePosition(position, { instant = false } = {}) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      const previousScrollBehavior = this.slider.style.scrollBehavior;

      if (instant) {
        this.slider.style.scrollBehavior = 'auto';
      }

      this.slider.scrollTo({
        left: position,
      });

      if (instant) {
        requestAnimationFrame(() => {
          this.slider.style.scrollBehavior = previousScrollBehavior;
        });
      }
    }, this.announcerBarAnimationDelay);
  }

  update() {
    if (this.isFadeMode) {
      this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
      this.prevButton?.removeAttribute('disabled');
      this.nextButton?.removeAttribute('disabled');

      if (!this.sliderControlButtons.length) return;

      this.sliderControlButtons.forEach((link) => {
        link.classList.remove('slider-counter__link--active');
        link.removeAttribute('aria-current');
      });
      this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
      this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
      return;
    }

    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    if (this.autoplayButtonIsSetToPlay) {
      this.pause();
    } else {
      this.manualPauseByUser = false;
      this.pauseAfterCurrentProgress = false;
      this.play();
    }
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearTimeout(this.autoplay);

    const isResuming = typeof this.autoplayRemainingTime === 'number' && this.autoplayRemainingTime > 0;
    const delay = isResuming ? this.autoplayRemainingTime : this.autoplaySpeed;
    this.autoplayDelay = delay;
    this.autoplayStartedAt = performance.now();

    this.autoplay = setTimeout(() => {
      this.autoplayRemainingTime = null;
      this.autoplayStartedAt = null;
      this.autoplayDelay = null;

      if (this.pauseAfterCurrentProgress) {
        this.pauseAfterCurrentProgress = false;
        this.pause();
        this.autoplayRemainingTime = null;
        this.updateProgressSegments({ restartAnimation: false, animate: false, completeActive: true });
        return;
      }

      this.autoRotateSlides();

      if (this.isAutoRotateEnabled() && (!this.sliderAutoplayButton || this.autoplayButtonIsSetToPlay)) {
        this.play();
      }
    }, delay);

    this.setProgressPlayState('running');
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearTimeout(this.autoplay);

    if (typeof this.autoplayStartedAt === 'number' && typeof this.autoplayDelay === 'number') {
      const elapsed = performance.now() - this.autoplayStartedAt;
      this.autoplayRemainingTime = Math.max(this.autoplayDelay - elapsed, 0);
      this.autoplayStartedAt = null;
      this.autoplayDelay = null;
    }

    this.setProgressPlayState('paused');
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    if (this.isFadeMode) {
      const nextPage = (this.currentPage % this.sliderItems.length) + 1;
      this.goToSlide(nextPage);
      this.applyAnimationToAnnouncementBar('next');
      return;
    }

    const isWrappingToFirstSlide = this.currentPage === this.sliderItems.length;
    const slideScrollPosition = isWrappingToFirstSlide ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition, { instant: isWrappingToFirstSlide });
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    if (this.isFadeMode) {
      this.setFadeSlideVisibility();
      this.wasClicked = false;
      return;
    }

    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        item.classList.add('is-active');
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        item.classList.remove('is-active');
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    if (this.isFadeMode) {
      const nextPage = this.sliderControlLinksArray.indexOf(event.currentTarget) + 1;
      if (nextPage < 1) return;
      this.goToSlide(nextPage);
      this.restartAutoplayTimer();
      return;
    }

    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
    this.restartAutoplayTimer();
  }

  goToSlide(nextPage) {
    if (this.isFadeMode) {
      if (nextPage === this.currentPage) return;
      const previousPage = this.currentPage;
      this.currentPage = nextPage;
      this.setFadeSlideVisibility(previousPage);
      this.update();
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
      return;
    }

    this.currentPage = nextPage;
    this.setSlideVisibility();
    this.update();
  }

  setFadeSlideVisibility(previousPage = null) {
    if (this.fadeCleanupTimeout) clearTimeout(this.fadeCleanupTimeout);

    this.sliderItemsToShow.forEach((item, index) => {
      const isActive = index === this.currentPage - 1;
      const isLeaving = previousPage !== null && index === previousPage - 1 && !isActive;
      const linkElements = item.querySelectorAll('a');

      if (isActive) {
        item.classList.add('is-active');
        item.classList.remove('is-leaving');
        if (linkElements.length) {
          linkElements.forEach((button) => button.removeAttribute('tabindex'));
        }
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
        return;
      }

      item.classList.remove('is-active');
      if (isLeaving) {
        item.classList.add('is-leaving');
      } else {
        item.classList.remove('is-leaving');
      }

      if (linkElements.length) {
        linkElements.forEach((button) => button.setAttribute('tabindex', '-1'));
      }
      item.setAttribute('aria-hidden', 'true');
      item.setAttribute('tabindex', '-1');
    });

    if (previousPage !== null) {
      this.fadeCleanupTimeout = setTimeout(() => {
        const previousSlide = this.sliderItemsToShow[previousPage - 1];
        if (previousSlide) previousSlide.classList.remove('is-leaving');
      }, this.fadeDuration);
    }
  }

  isAutoRotateEnabled() {
    return this.slider?.getAttribute('data-autoplay') === 'true';
  }

  setProgressPlayState(state) {
    if (!this.progressContainer) return;
    this.progressContainer.style.setProperty('--slideshow-progress-play-state', state);
  }

  updateProgressSegments({ restartAnimation = false, animate = null, completeActive = false } = {}) {
    if (!this.progressSegments.length) return;

    this.progressSegments.forEach((segment, index) => {
      const isBeforeActive = index < this.currentPage - 1;
      const isActive = index === this.currentPage - 1;

      segment.classList.remove('is-complete', 'is-active', 'is-animating');
      segment.removeAttribute('aria-current');

      if (isBeforeActive) {
        segment.classList.add('is-complete');
      } else if (isActive) {
        segment.classList.add('is-active');
        if (completeActive) segment.classList.add('is-complete');
        segment.setAttribute('aria-current', 'true');
      }
    });

    const shouldAnimate =
      animate !== null
        ? animate
        : this.isAutoRotateEnabled() &&
          !this.manualPauseByUser &&
          (!this.sliderAutoplayButton || this.autoplayButtonIsSetToPlay);

    if (!shouldAnimate) return;

    const activeSegment = this.progressSegments[this.currentPage - 1];
    if (!activeSegment) return;

    if (restartAnimation) {
      activeSegment.classList.remove('is-animating');
      void activeSegment.offsetWidth;
    }

    activeSegment.classList.add('is-animating');
  }

  pauseOnHover() {
    if (!this.isAutoRotateEnabled()) return;
    this.pause();
  }

  resumeOnHover() {
    if (!this.isAutoRotateEnabled()) return;
    if (this.manualPauseByUser) return;
    this.play();
  }

  restartAutoplayTimer() {
    if (!this.isAutoRotateEnabled()) return;
    if (this.sliderAutoplayButton && !this.autoplayButtonIsSetToPlay) return;
    this.manualPauseByUser = false;
    this.pauseAfterCurrentProgress = false;
    this.autoplayRemainingTime = null;
    this.play();
  }

  requestPauseAfterProgressComplete() {
    if (!this.isAutoRotateEnabled()) return;

    this.pauseAfterCurrentProgress = true;
    this.manualPauseByUser = true;
    this.autoplayRemainingTime = null;
    this.autoplayStartedAt = null;
    this.autoplayDelay = null;

    if (this.sliderAutoplayButton && this.autoplayButtonIsSetToPlay) {
      this.togglePlayButtonState(true);
      this.autoplayButtonIsSetToPlay = false;
    }

    this.updateProgressSegments({ restartAnimation: true, animate: true, completeActive: false });
    this.play();
  }

  initSwipeGestures() {
    if (!this.slider) return;

    if (this.swipeAbortController) {
      this.swipeAbortController.abort();
    }
    this.swipeAbortController = new AbortController();

    this.mobileViewportQuery = window.matchMedia('(max-width: 749px)');
    this.swipeThresholdMin = 24;
    this.swipeThresholdMax = 72;
    this.swipeState = {
      isTracking: false,
      pointerId: null,
      startX: 0,
      startY: 0,
      startedOnInteractive: false,
    };
    this.suppressClickAfterSwipe = false;

    this.slider.style.touchAction = 'pan-y';

    this.slider.addEventListener(
      'pointerdown',
      (event) => {
        if (!this.mobileViewportQuery.matches) return;
        if (this.sliderItemsToShow.length < 2) return;
        if (event.pointerType !== 'touch' && event.pointerType !== 'pen') return;
        if (!event.isPrimary) return;

        const startedOnInteractiveElement = Boolean(
          event.target?.closest?.('a, button, input, select, textarea, summary, [role="button"]')
        );

        this.swipeState.isTracking = true;
        this.swipeState.pointerId = event.pointerId;
        this.swipeState.startX = event.clientX;
        this.swipeState.startY = event.clientY;
        this.swipeState.startedOnInteractive = startedOnInteractiveElement;
      },
      { signal: this.swipeAbortController.signal }
    );

    this.slider.addEventListener(
      'pointerup',
      (event) => {
        if (!this.swipeState.isTracking) return;
        if (event.pointerId !== this.swipeState.pointerId) return;

        const deltaX = event.clientX - this.swipeState.startX;
        const deltaY = event.clientY - this.swipeState.startY;
        const swipeThreshold = Math.min(
          this.swipeThresholdMax,
          Math.max(this.swipeThresholdMin, Math.round(window.innerWidth * 0.08))
        );
        const shouldSwipe =
          Math.abs(deltaX) >= swipeThreshold && Math.abs(deltaX) > Math.abs(deltaY);

        this.swipeState.isTracking = false;
        this.swipeState.pointerId = null;
        const startedOnInteractiveElement = this.swipeState.startedOnInteractive;
        this.swipeState.startedOnInteractive = false;

        if (!shouldSwipe) return;
        if (startedOnInteractiveElement) return;

        this.suppressClickAfterSwipe = true;
        window.setTimeout(() => {
          this.suppressClickAfterSwipe = false;
        }, 120);

        if (deltaX < 0) {
          this.nextButton?.click();
        } else {
          this.prevButton?.click();
        }
      },
      { signal: this.swipeAbortController.signal }
    );

    this.slider.addEventListener(
      'pointercancel',
      (event) => {
        if (event.pointerId !== this.swipeState.pointerId) return;
        this.swipeState.isTracking = false;
        this.swipeState.pointerId = null;
        this.swipeState.startedOnInteractive = false;
      },
      { signal: this.swipeAbortController.signal }
    );

    this.slider.addEventListener(
      'pointerleave',
      (event) => {
        if (event.pointerId !== this.swipeState.pointerId) return;
        this.swipeState.isTracking = false;
        this.swipeState.pointerId = null;
        this.swipeState.startedOnInteractive = false;
      },
      { signal: this.swipeAbortController.signal }
    );

    this.slider.addEventListener(
      'click',
      (event) => {
        if (!this.suppressClickAfterSwipe) return;
        event.preventDefault();
        event.stopPropagation();
      },
      {
        capture: true,
        signal: this.swipeAbortController.signal,
      }
    );
  }

  disconnectedCallback() {
    this.swipeAbortController?.abort();
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.onChangeHandler = this.onChange.bind(this);
  }

  connectedCallback() {
    this.addEventListener('change', this.onChangeHandler);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChangeHandler);
  }

  onChange(event) {
    const target = event.target;
    this.updateSelectionMetadata(event);

    publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
      data: {
        event,
        target,
        selectedOptionValues: this.selectedOptionValues,
      },
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      const previouslySelectedOption = Array.from(target.options).find((option) => option.getAttribute('selected'));
      previouslySelectedOption?.removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

function getCardSizeQuickAddVariants(quickAddContainer) {
  if (!quickAddContainer) return [];
  if (quickAddContainer.cardSizeVariants) return quickAddContainer.cardSizeVariants;

  const variantsJson = quickAddContainer.querySelector('[data-card-size-variants]');
  if (!variantsJson) {
    quickAddContainer.cardSizeVariants = [];
    return quickAddContainer.cardSizeVariants;
  }

  try {
    quickAddContainer.cardSizeVariants = JSON.parse(variantsJson.textContent.trim());
  } catch (error) {
    console.error(error);
    quickAddContainer.cardSizeVariants = [];
  }

  return quickAddContainer.cardSizeVariants;
}

function getVariantOptionValue(variant, optionIndex) {
  if (!variant || !Number.isInteger(optionIndex) || optionIndex < 0) return null;
  if (Array.isArray(variant.options) && variant.options.length > optionIndex) return variant.options[optionIndex];
  const optionKey = `option${optionIndex + 1}`;
  return variant[optionKey] ?? null;
}

function updateCardSizeQuickAdd(card) {
  const quickAddContainer = card?.querySelector('[data-card-size-quick-add]');
  if (!quickAddContainer) return;

  const sizeButtons = Array.from(quickAddContainer.querySelectorAll('[data-card-size-option]'));
  if (!sizeButtons.length) return;

  const variants = getCardSizeQuickAddVariants(quickAddContainer);
  if (!variants.length) return;

  const sizeOptionIndex = parseInt(quickAddContainer.dataset.sizeOptionIndex, 10);
  const colorOptionIndex = parseInt(quickAddContainer.dataset.colorOptionIndex, 10);
  const activeSwatch = card.querySelector('[data-card-swatch].is-active');
  const activeSwatchVariantId = activeSwatch?.dataset.variantId;
  const selectedColorVariant = variants.find((variant) => String(variant.id) === String(activeSwatchVariantId));
  const selectedColorValue =
    getVariantOptionValue(selectedColorVariant, colorOptionIndex) ||
    activeSwatch?.dataset.colorValue ||
    activeSwatch?.dataset.variantLabel ||
    null;

  sizeButtons.forEach((button) => {
    const sizeValue = button.dataset.sizeValue;
    const sizeMatchingVariants = variants.filter((variant) => {
      const variantSizeValue = getVariantOptionValue(variant, sizeOptionIndex);
      return variantSizeValue === sizeValue;
    });

    let matchingVariants = sizeMatchingVariants;
    if (Number.isInteger(colorOptionIndex) && colorOptionIndex >= 0 && selectedColorValue) {
      const colorMatchingVariants = sizeMatchingVariants.filter((variant) => {
        const variantColorValue = getVariantOptionValue(variant, colorOptionIndex);
        return variantColorValue === selectedColorValue;
      });

      matchingVariants = colorMatchingVariants;
    }

    const preferredVariant = matchingVariants.find((variant) => variant.available) || matchingVariants[0];
    const isAvailable = Boolean(preferredVariant?.available);

    button.classList.toggle('is-unavailable', !isAvailable);
    button.disabled = !isAvailable;
    button.setAttribute('aria-disabled', String(!isAvailable));
    if (!isAvailable) button.classList.remove('is-selected');

    if (preferredVariant?.id) {
      button.dataset.variantId = String(preferredVariant.id);
      button.dataset.variantAvailable = String(Boolean(preferredVariant.available));
    } else {
      delete button.dataset.variantId;
      delete button.dataset.variantAvailable;
    }
  });
}

function initCardSizeQuickAdd(root = document) {
  root.querySelectorAll('[data-product-card]').forEach((card) => updateCardSizeQuickAdd(card));
}

function addCardSizeVariantToCart({ card, button, variantId }) {
  const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  const config = fetchConfig('javascript');
  config.headers['X-Requested-With'] = 'XMLHttpRequest';
  delete config.headers['Content-Type'];

  const formData = new FormData();
  formData.append('id', variantId);
  formData.append('quantity', '1');

  if (cart) {
    formData.append(
      'sections',
      cart.getSectionsToRender().map((section) => section.id)
    );
    formData.append('sections_url', window.location.pathname);
    cart.setActiveElement(button);
  }

  config.body = formData;

  button.classList.add('is-loading');

  fetch(`${routes.cart_add_url}`, config)
    .then((response) => response.json())
    .then((response) => {
      if (response.status) {
        publish(PUB_SUB_EVENTS.cartError, {
          source: 'card-size-quick-add',
          productVariantId: variantId,
          errors: response.errors || response.description,
          message: response.message,
        });
        return;
      }

      if (!cart) {
        window.location = window.routes.cart_url;
        return;
      }

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'card-size-quick-add',
        productVariantId: variantId,
        cartData: response,
      });

      if (cart.classList.contains('is-empty')) {
        cart.classList.remove('is-empty');
      }

      cart.renderContents(response);
    })
    .catch((error) => {
      console.error(error);
    })
    .finally(() => {
      button.classList.remove('is-loading');
      updateCardSizeQuickAdd(card);
    });
}

function syncCardSwatchRow(row) {
  const track = row.querySelector('[data-card-swatches-track]') || row;
  const swatches = Array.from(track.querySelectorAll('[data-card-swatch]'));
  const moreLink = row.querySelector('[data-card-swatches-more]');

  if (!swatches.length || !moreLink) return;

  swatches.forEach((swatch) => {
    swatch.hidden = false;
  });

  moreLink.hidden = true;
  moreLink.textContent = '';
  moreLink.style.visibility = '';

  const fitsTrack = () => track.scrollWidth <= track.clientWidth + 1;

  if (fitsTrack()) return;

  for (let visibleCount = swatches.length - 1; visibleCount >= 0; visibleCount--) {
    swatches.forEach((swatch, index) => {
      swatch.hidden = index >= visibleCount;
    });

    const hiddenCount = swatches.length - visibleCount;
    if (hiddenCount > 0) {
      moreLink.hidden = false;
      moreLink.textContent = `+${hiddenCount}`;
      moreLink.style.visibility = 'hidden';
    } else {
      moreLink.hidden = true;
      moreLink.textContent = '';
      moreLink.style.visibility = '';
    }

    if (fitsTrack()) {
      moreLink.style.visibility = '';
      return;
    }
  }

  swatches.forEach((swatch) => {
    swatch.hidden = true;
  });
  moreLink.hidden = false;
  moreLink.textContent = `+${swatches.length}`;
  moreLink.style.visibility = '';
}

function initCardSwatchLayouts(root = document) {
  const rows = root.querySelectorAll('[data-card-swatches]');

  rows.forEach((row) => {
    if (!row.dataset.cardSwatchesBound) {
      row.dataset.cardSwatchesBound = 'true';

      if ('ResizeObserver' in window) {
        row.cardSwatchesObserver = new ResizeObserver(() => syncCardSwatchRow(row));
        row.cardSwatchesObserver.observe(row);
      }
    }

    syncCardSwatchRow(row);
  });
}

function hashStringToInt(value) {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = (hash << 5) - hash + value.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

function createSeededRandom(seed) {
  let state = seed % 2147483647;
  if (state <= 0) state += 2147483646;
  return () => {
    state = (state * 16807) % 2147483647;
    return (state - 1) / 2147483646;
  };
}

function getCardSeed(card) {
  const linkHref = card?.querySelector('[data-card-product-link]')?.getAttribute('href') || '';
  const headingText = card?.querySelector('.card__heading a')?.textContent?.trim() || '';
  const swatchIds = Array.from(card?.querySelectorAll('[data-card-swatch]') || [])
    .map((swatch) => swatch.dataset.variantId || swatch.dataset.colorValue || '')
    .join('|');
  return hashStringToInt(`${linkHref}::${headingText}::${swatchIds}`);
}

function applyCardSwatchSelection(swatch, { updateActiveState = true } = {}) {
  if (!swatch) return;

  const card = swatch.closest('[data-product-card]');
  if (!card) return;

  if (updateActiveState) {
    card.querySelectorAll('[data-card-swatch].is-active').forEach((element) => element.classList.remove('is-active'));
    swatch.classList.add('is-active');
  }

  const variantUrl = swatch.dataset.variantUrl;
  if (variantUrl) {
    card.querySelectorAll('[data-card-product-link]').forEach((link) => {
      link.setAttribute('href', variantUrl);
    });

    card.querySelectorAll('[data-product-url]').forEach((element) => {
      element.dataset.productUrl = variantUrl;
    });
  }

  const variantId = swatch.dataset.variantId;
  const variantIdInput = card.querySelector('[data-card-variant-id]');
  if (variantId && variantIdInput) {
    variantIdInput.value = variantId;
  }

  const imageSrc = swatch.dataset.variantImageSrc;
  if (imageSrc) {
    card.querySelectorAll('[data-card-primary-image], [data-card-secondary-image]').forEach((image) => {
      image.setAttribute('src', imageSrc);

      if (swatch.dataset.variantImageSrcset) {
        image.setAttribute('srcset', swatch.dataset.variantImageSrcset);
      }

      if (swatch.dataset.variantImageAlt) {
        image.setAttribute('alt', swatch.dataset.variantImageAlt);
      }

      if (swatch.dataset.variantImageWidth) {
        image.setAttribute('width', swatch.dataset.variantImageWidth);
      }

      if (swatch.dataset.variantImageHeight) {
        image.setAttribute('height', swatch.dataset.variantImageHeight);
      }
    });
  }

  const addButton = card.querySelector('[data-card-add-button]');
  if (addButton) {
    const isAvailable = swatch.dataset.variantAvailable === 'true';
    addButton.disabled = !isAvailable;

    const mainLabel = addButton.querySelector('span:not(.sold-out-message)');
    const soldOutLabel = addButton.querySelector('.sold-out-message');

    if (mainLabel) {
      mainLabel.textContent = isAvailable
        ? addButton.dataset.addToCartLabel || mainLabel.textContent
        : addButton.dataset.soldOutLabel || mainLabel.textContent;
    }

    if (soldOutLabel && addButton.dataset.soldOutLabel) {
      soldOutLabel.textContent = addButton.dataset.soldOutLabel;
    }
  }

  const price = card.querySelector('.card-information .price');
  if (price) {
    const isOnSale = swatch.dataset.variantOnSale === 'true';
    const regularPriceNodes = price.querySelectorAll('.price__regular .price-item--regular');
    const saleRegularNode = price.querySelector('.price__sale .price-item--regular');
    const salePriceNode = price.querySelector('.price__sale .price-item--sale');

    if (isOnSale) {
      price.classList.add('price--on-sale');

      if (saleRegularNode && swatch.dataset.variantComparePrice) {
        saleRegularNode.textContent = swatch.dataset.variantComparePrice;
      }

      if (salePriceNode && swatch.dataset.variantPrice) {
        salePriceNode.textContent = swatch.dataset.variantPrice;
      }
    } else {
      price.classList.remove('price--on-sale');

      regularPriceNodes.forEach((node) => {
        if (swatch.dataset.variantPrice) node.textContent = swatch.dataset.variantPrice;
      });
    }

    if (!isOnSale && salePriceNode && swatch.dataset.variantPrice) {
      salePriceNode.textContent = swatch.dataset.variantPrice;
    }
  }

  const swatchRow = swatch.closest('[data-card-swatches]');
  if (swatchRow) {
    syncCardSwatchRow(swatchRow);
  }

  updateCardSizeQuickAdd(card);
}

function initCardSwatchesWithSeededOrder(root = document) {
  root.querySelectorAll('[data-product-card]').forEach((card) => {
    if (card.dataset.swatchesInitialized === 'true') return;

    const track = card.querySelector('[data-card-swatches-track]');
    if (!track) return;

    const swatches = Array.from(track.querySelectorAll('[data-card-swatch]'));
    if (!swatches.length) return;

    const random = createSeededRandom(getCardSeed(card));

    for (let i = swatches.length - 1; i > 0; i--) {
      const randomIndex = Math.floor(random() * (i + 1));
      const current = swatches[i];
      const randomItem = swatches[randomIndex];
      if (current === randomItem) continue;
      track.insertBefore(current, randomItem);
      swatches[i] = randomItem;
      swatches[randomIndex] = current;
    }

    const primarySwatch = track.querySelector('[data-card-swatch]');
    if (primarySwatch) {
      applyCardSwatchSelection(primarySwatch);
    }

    card.dataset.swatchesInitialized = 'true';
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initCardSwatchesWithSeededOrder();
    initCardSwatchLayouts();
    initCardSizeQuickAdd();
  });
} else {
  initCardSwatchesWithSeededOrder();
  initCardSwatchLayouts();
  initCardSizeQuickAdd();
}

window.addEventListener(
  'resize',
  debounce(() => {
    initCardSwatchLayouts();
    initCardSizeQuickAdd();
  }, 100)
);

document.addEventListener(
  'pointerdown',
  (event) => {
    const eventTargetElement =
      event.target instanceof Element ? event.target : event.target?.parentElement || null;
    if (!eventTargetElement) return;

    if (eventTargetElement.closest('[data-card-size-quick-add]')) {
      event.stopPropagation();
    }
  },
  true
);

document.addEventListener('click', (event) => {
  if (event.defaultPrevented) return;

  const eventPath = event.composedPath ? event.composedPath() : [];
  const eventElementPath = eventPath.filter((node) => node instanceof Element);
  const eventTargetElement =
    event.target instanceof Element ? event.target : event.target?.parentElement || null;

  const card = eventElementPath.find((node) => node.matches('[data-product-card]')) || eventTargetElement?.closest?.('[data-product-card]');
  if (!card) return;

  const cardQuickAdd = card.querySelector('[data-card-size-quick-add]');
  if (cardQuickAdd) {
    const rect = cardQuickAdd.getBoundingClientRect();
    const isInsideQuickAddRect =
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom;
    if (isInsideQuickAddRect) return;
  }

  const clickedInsideSizeQuickAdd = eventElementPath.some((node) => node.matches('[data-card-size-quick-add]'));
  if (clickedInsideSizeQuickAdd) return;

  const clickedInsideSwatchUi = eventElementPath.some((node) =>
    node.matches('[data-card-swatch], [data-card-swatches-more]')
  );
  if (clickedInsideSwatchUi) return;

  const clickedInteractiveElement = eventElementPath.some((node) =>
    node.matches('a, button, input, select, textarea, summary, [role="button"]')
  );
  if (clickedInteractiveElement) return;

  // Navigate only for direct media-area clicks, not generic card-inner clicks.
  const clickedImageArea = eventElementPath.some((node) =>
    node.matches('.card__media, .media, [data-card-primary-image], [data-card-secondary-image]')
  );
  if (!clickedImageArea) {
    return;
  }

  const link = card.querySelector('[data-card-product-link]');
  if (link?.href) {
    window.location.href = link.href;
  }
});

document.addEventListener(
  'click',
  (event) => {
    const eventTargetElement =
      event.target instanceof Element ? event.target : event.target?.parentElement || null;
    if (!eventTargetElement) return;

    const sizeButton = eventTargetElement.closest('[data-card-size-option]');
    if (!sizeButton) return;

    if (sizeButton.disabled || sizeButton.classList.contains('is-unavailable')) return;

    // Keep card-level navigation handlers from hijacking size clicks.
    event.preventDefault();
    event.stopImmediatePropagation();
    event.stopPropagation();

    const card = sizeButton.closest('[data-product-card]');
    if (!card) return;

    const variantId = sizeButton.dataset.variantId;
    if (!variantId) return;

    card
      .querySelectorAll('[data-card-size-option].is-selected')
      .forEach((button) => button.classList.remove('is-selected'));
    sizeButton.classList.add('is-selected');

    addCardSizeVariantToCart({ card, button: sizeButton, variantId });
  },
  true
);

document.addEventListener('click', (event) => {
  const swatch = event.target.closest('[data-card-swatch]');
  if (!swatch) return;

  event.preventDefault();
  event.stopPropagation();
  applyCardSwatchSelection(swatch);
});

document.addEventListener('mouseover', (event) => {
  const swatch = event.target.closest('[data-card-swatch]');
  if (!swatch) return;

  const relatedTarget = event.relatedTarget;
  if (relatedTarget instanceof Element && swatch.contains(relatedTarget)) return;

  applyCardSwatchSelection(swatch, { updateActiveState: false });
});

document.addEventListener('mouseout', (event) => {
  const swatch = event.target.closest('[data-card-swatch]');
  if (!swatch) return;

  const card = swatch.closest('[data-product-card]');
  if (!card) return;

  const relatedTarget = event.relatedTarget;
  const nextSwatch =
    relatedTarget instanceof Element ? relatedTarget.closest('[data-card-swatch]') : null;

  // If pointer moved to another swatch in the same card, let that swatch preview take over.
  if (nextSwatch && nextSwatch.closest('[data-product-card]') === card) return;

  const activeSwatch = card.querySelector('[data-card-swatch].is-active');
  if (!activeSwatch) return;

  applyCardSwatchSelection(activeSwatch, { updateActiveState: false });
});

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
          initCardSwatchesWithSeededOrder(this);
          initCardSwatchLayouts(this);
          initCardSizeQuickAdd(this);
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}
// cache bump 2026-05-11
